import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { MapPin, Users, Music, UtensilsCrossed, BriefcaseBusiness, Send, Camera, ShieldCheck } from 'lucide-react';

const boolLabel = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
};

export default function BarOwnerProfilePage() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const { user, isBartender } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [applyingJobId, setApplyingJobId] = useState('');

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/bars/${ownerId}/profile`);
      const data = response.data;
      setProfileData(data);
      setSelectedImage((data?.profile?.gallery_images || [])[0] || '');
    } catch (err) {
      console.error('Failed to load bar owner profile:', err);
      setError(err.response?.data?.detail || 'Unable to load this bar profile right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ownerId) return;
    loadProfile();
  }, [ownerId]);

  const allVibeTags = useMemo(() => {
    const profile = profileData?.profile || {};
    return [...(profile.vibe_tags || []), ...(profile.custom_vibe_tags || [])];
  }, [profileData]);

  const shiftEnvironmentRows = useMemo(() => {
    const environment = profileData?.profile?.shift_environment || {};
    const rows = [];

    if ((environment.busiest_nights || []).length > 0) {
      rows.push({ label: 'Busiest Nights', value: environment.busiest_nights.join(', '), icon: Users });
    }
    if (environment.dress_code) {
      rows.push({ label: 'Dress Code', value: environment.dress_code, icon: BriefcaseBusiness });
    }
    if (environment.service_mode) {
      const map = {
        cocktail_focused: 'Cocktail-focused',
        volume_focused: 'Volume-focused',
        balanced: 'Balanced',
      };
      rows.push({ label: 'Service Style', value: map[environment.service_mode] || environment.service_mode, icon: BriefcaseBusiness });
    }
    if (typeof environment.food_service === 'boolean') {
      rows.push({ label: 'Food Service', value: boolLabel(environment.food_service), icon: UtensilsCrossed });
    }
    if (typeof environment.live_entertainment === 'boolean') {
      rows.push({ label: 'Live Entertainment', value: boolLabel(environment.live_entertainment), icon: Music });
    }

    return rows;
  }, [profileData]);

  const handleApplyToJob = async (jobId) => {
    try {
      setApplyingJobId(jobId);
      await axios.post(`/jobs/${jobId}/apply`, {
        cover_letter: 'Interested in this opportunity and would love to connect.',
      });
      toast.success('Application submitted successfully');
    } catch (err) {
      console.error('Failed to apply for job:', err);
      toast.error(err.response?.data?.detail || 'Unable to submit application');
    } finally {
      setApplyingJobId('');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bar-owner-profile-loading-state">
        <Skeleton className="h-12 w-64 mb-4" />
        <Skeleton className="h-8 w-full mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="bar-owner-profile-error-state">
        <Card className="bg-white/5 border border-red-500/20">
          <CardContent className="p-6 text-center">
            <p className="text-red-400 mb-4" data-testid="bar-owner-profile-error-text">{error}</p>
            <Button variant="outline" onClick={loadProfile} data-testid="bar-owner-profile-retry-button">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const profile = profileData?.profile || {};
  const owner = profileData?.owner || {};
  const openJobs = profileData?.open_jobs || [];
  const galleryImages = profile.gallery_images || [];
  const primaryImage = selectedImage || galleryImages[0] || '';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bar-owner-profile-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="bar-owner-profile-business-name">
            {profile.business_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'Bar Profile'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {profile.establishment_type && <Badge data-testid="bar-owner-profile-establishment-type">{profile.establishment_type}</Badge>}
            {profile.verification_status === 'approved' && (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid="bar-owner-profile-verified-badge">
                <ShieldCheck className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
            {(profile.location?.city || profile.location?.state) && (
              <span className="text-sm text-muted-foreground inline-flex items-center" data-testid="bar-owner-profile-location">
                <MapPin className="h-3 w-3 mr-1" />
                {[profile.location?.city, profile.location?.state].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        </div>

        {user?.id === owner.id ? (
          <Button onClick={() => navigate('/profile')} data-testid="bar-owner-profile-edit-button">Edit My Bar Profile</Button>
        ) : (
          <Button variant="outline" onClick={() => navigate('/messages?user=' + owner.id)} data-testid="bar-owner-profile-message-owner-button">
            <Send className="h-4 w-4 mr-2" />Message Owner
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white/5 border border-white/10" data-testid="bar-owner-profile-gallery-card">
            <CardHeader>
              <CardTitle className="flex items-center" data-testid="bar-owner-profile-gallery-title">
                <Camera className="h-4 w-4 mr-2" />Gallery
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!primaryImage ? (
                <div className="rounded-md border border-dashed border-white/10 p-8 text-center text-muted-foreground" data-testid="bar-owner-profile-gallery-empty">
                  No photos uploaded yet.
                </div>
              ) : (
                <>
                  <div className="rounded-lg overflow-hidden border border-white/10 mb-3" data-testid="bar-owner-profile-primary-image-wrapper">
                    <img src={primaryImage} alt="Bar showcase" className="w-full h-72 object-cover" data-testid="bar-owner-profile-primary-image" />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="bar-owner-profile-thumbnails-grid">
                    {galleryImages.map((image, index) => (
                      <button
                        key={`${image.slice(0, 18)}-${index}`}
                        type="button"
                        className={`rounded-md overflow-hidden border ${primaryImage === image ? 'border-primary' : 'border-white/10'}`}
                        onClick={() => setSelectedImage(image)}
                        data-testid={`bar-owner-profile-thumbnail-${index}`}
                      >
                        <img src={image} alt={`Bar gallery ${index + 1}`} className="w-full h-20 object-cover" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/10" data-testid="bar-owner-profile-about-card">
            <CardHeader>
              <CardTitle data-testid="bar-owner-profile-about-title">About Our Bar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(profile.about_bar || profile.description) ? (
                <p className="text-muted-foreground leading-relaxed" data-testid="bar-owner-profile-about-text">
                  {profile.about_bar || profile.description}
                </p>
              ) : (
                <p className="text-muted-foreground" data-testid="bar-owner-profile-about-empty">No about section added yet.</p>
              )}

              {profile.atmosphere && (
                <div data-testid="bar-owner-profile-atmosphere-row"><p className="text-sm font-medium text-white">Atmosphere</p><p className="text-sm text-muted-foreground">{profile.atmosphere}</p></div>
              )}
              {profile.clientele && (
                <div data-testid="bar-owner-profile-clientele-row"><p className="text-sm font-medium text-white">Clientele</p><p className="text-sm text-muted-foreground">{profile.clientele}</p></div>
              )}
              {profile.culture && (
                <div data-testid="bar-owner-profile-culture-row"><p className="text-sm font-medium text-white">Culture</p><p className="text-sm text-muted-foreground">{profile.culture}</p></div>
              )}
              {profile.bartender_expectations && (
                <div data-testid="bar-owner-profile-expectations-row"><p className="text-sm font-medium text-white">What They Look For in Bartenders</p><p className="text-sm text-muted-foreground">{profile.bartender_expectations}</p></div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/10" data-testid="bar-owner-profile-open-jobs-card">
            <CardHeader>
              <CardTitle data-testid="bar-owner-profile-open-jobs-title">Open Jobs ({profileData?.open_jobs_count || 0})</CardTitle>
              <CardDescription data-testid="bar-owner-profile-open-jobs-description">Apply directly to active opportunities from this bar.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {openJobs.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="bar-owner-profile-open-jobs-empty">No active jobs posted right now.</div>
              ) : (
                openJobs.map((job) => (
                  <div key={job.id} className="rounded-lg border border-white/10 p-3" data-testid={`bar-owner-profile-job-card-${job.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <p className="font-medium text-white" data-testid={`bar-owner-profile-job-title-${job.id}`}>{job.title}</p>
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`bar-owner-profile-job-meta-${job.id}`}>
                          {[job.location?.city, job.location?.state].filter(Boolean).join(', ')}
                          {job.hourly_rate ? ` • $${job.hourly_rate}/hr` : ''}
                        </p>
                      </div>
                      {isBartender ? (
                        <Button
                          size="sm"
                          onClick={() => handleApplyToJob(job.id)}
                          disabled={applyingJobId === job.id}
                          data-testid={`bar-owner-profile-apply-job-${job.id}`}
                        >
                          {applyingJobId === job.id ? 'Applying...' : 'Apply'}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/jobs`)} data-testid={`bar-owner-profile-view-jobs-${job.id}`}>
                          View in Jobs
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-white/5 border border-white/10" data-testid="bar-owner-profile-vibe-card">
            <CardHeader>
              <CardTitle data-testid="bar-owner-profile-vibe-title">Bar Vibe</CardTitle>
            </CardHeader>
            <CardContent>
              {allVibeTags.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="bar-owner-profile-vibe-empty">No vibe tags shared yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="bar-owner-profile-vibe-tags-wrap">
                  {allVibeTags.map((tag) => (
                    <Badge key={tag} variant="outline" data-testid={`bar-owner-profile-vibe-tag-${tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/10" data-testid="bar-owner-profile-shift-environment-card">
            <CardHeader>
              <CardTitle data-testid="bar-owner-profile-shift-environment-title">Shift Environment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {shiftEnvironmentRows.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="bar-owner-profile-shift-environment-empty">No shift environment details provided yet.</p>
              ) : (
                shiftEnvironmentRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-2" data-testid={`bar-owner-profile-shift-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <row.icon className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{row.label}</p>
                      <p className="text-sm text-white">{row.value}</p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {user?.id !== owner.id && (
            <Card className="bg-primary/10 border border-primary/30" data-testid="bar-owner-profile-cta-card">
              <CardHeader>
                <CardTitle data-testid="bar-owner-profile-cta-title">Interested in this bar?</CardTitle>
                <CardDescription data-testid="bar-owner-profile-cta-description">Message the owner or apply to open shifts now.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button className="w-full" onClick={() => navigate('/messages?user=' + owner.id)} data-testid="bar-owner-profile-cta-message-button">
                  <Send className="h-4 w-4 mr-2" />Message Owner
                </Button>
                <Button className="w-full" variant="outline" onClick={() => navigate('/jobs')} data-testid="bar-owner-profile-cta-view-jobs-button">
                  View More Jobs
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
